import { useEffect, useMemo } from 'react';

import { flatConnectedAccounts, PERFORMANCE_EXTRACTION_STATUS } from 'src/data/performance';
import { captureMessage } from 'src/lib/sentry';
import { createStateSelectorHook } from 'src/lib/sweetState';
import { useErrorHandler } from 'src/services';
import { isCopiesExtractionError, useCurrentCustomerId, usePerformanceActions } from 'src/store';
import { PerformanceStore } from 'src/store/performance/PerformanceStore';

import { useChannelAccounts } from './useChannelAccounts';

const usePerformanceState = createStateSelectorHook(PerformanceStore, ['extractionStatusMap']);

export const useGetAccountsLastExtraction = () => {
  const currentCustomerId = useCurrentCustomerId();
  const handleError = useErrorHandler();
  const performanceActions = usePerformanceActions();

  // Get current extraction status to filter out accounts with errors
  const { extractionStatusMap } = usePerformanceState();
  const { processingAccounts } = useChannelAccounts();

  // Filter out accounts that are in error state to prevent endless loops
  const accountsToProcess = useMemo(() => {
    const flattenRunningAccounts = flatConnectedAccounts(processingAccounts, true);
    return flattenRunningAccounts.filter((account) => {
      const currentStatus = extractionStatusMap[account.channel]?.[account.id];
      return currentStatus !== PERFORMANCE_EXTRACTION_STATUS.error;
    });
  }, [extractionStatusMap, processingAccounts]);

  const shouldGetAccountsLastExtraction = currentCustomerId && accountsToProcess?.length > 0;

  useEffect(() => {
    if (!shouldGetAccountsLastExtraction) {
      return;
    }

    accountsToProcess.forEach((account) => {
      performanceActions.getLastExtraction(account.channel, account.id).catch((error) => {
        if (isCopiesExtractionError(error)) {
          captureMessage(`Auth error getting ${account.channel} last extraction`, {
            level: 'warning',
            extra: {
              error: error.toString(),
              source: 'useGetAccountsLastExtraction',
            },
          });
        } else {
          handleError(error);
        }
      });
    });
  }, [shouldGetAccountsLastExtraction, performanceActions, accountsToProcess, handleError]);
};
